<script setup>
import SideSlider from "~/components/templates/SideSlider.vue";
import {useQuotationStore} from "~/store/quotationStore.js";
import {useStartupStore} from "~/store/sturtupStore.js";
import InputsGroup from "~/components/atoms/Inputs/InputsGroup.vue";
import FormInput from "~/components/atoms/FormInput.vue";
import Form from "~/components/atoms/Form.vue";
import Text from "~/components/atoms/Text.vue";
import Button from "~/components/atoms/Button.vue";
import LeftIcon from "~/components/icons/LeftIcon.vue";
import CircleCheckboxIcon from "~/components/icons/CircleCheckboxIcon.vue";
import CartProductItem from "~/components/molecules/CartProductItem.vue";
import {useCustomFetch} from '~/composables/customFetch.js';
import {useCurrencyStore} from "~/store/currencyStore.js";
import Disclaimer from "~/components/molecules/Disclaimer.vue";

const store = useQuotationStore();
const startupStore = useStartupStore();
const currencyStore = useCurrencyStore();
const {t} = useI18n();

const addComment = ref(false);
const addProductMode = ref(false);
const search = ref('');
const products = ref([]);
const container = ref(null)
const success = ref(false)
const noProductsError = ref(false)
const quotationErrors = ref([])
const config = useRuntimeConfig()
const hasCustomCountertop = computed(() => store.products.some(
    product => product.product_type === 'Måttbeställda Bänkskiva'
))

watch(search, () => {
  if (search.value.length > 3) {
    searchProduct()
  }
})

const companyNumberMin = computed(() => config.public.locale === 'da' ? 8 : 10)

const companyNumberValidations = computed(() => [
  'required',
  `min:${companyNumberMin.value}`,
])


watch(addProductMode, () => {
  nextTick(() => {
    container.value.scrollTop = 0
  })
})

const searchProduct = () => {
  useCustomFetch('product/search/' + search.value, {
    query: {
      quotation: 1
    }
  }).then(({data}) => {
    products.value = data.value.products
  })
}

const addProduct = (product) => {
  noProductsError.value = false
  store.addProduct(product.image, product.name, product.sku, product.unit, product.box_per_unit, product.url, product.niche, product.status, product.product_type)
  addProductMode.value = false
}

const send = (formData) => {
  quotationErrors.value = []

  if (!store.products.length) {
    noProductsError.value = true
    return
  }

  formData.append('products', JSON.stringify(store.products).replace(/</g, '\\u003C')  // <
      .replace(/>/g, '\\u003E')  // >
      .replace(/&/g, '\\u0026')  // &
      .replace(/\u2028/g, '\\u2028')  // Line separator
      .replace(/\u2029/g, '\\u2029')) // Paragraph separator)

  if (hasCustomCountertop.value && store.form.files && store.form.files.length) {
    store.form.files.forEach((file, index) => {
      formData.append(`files[${index}]`, file)
    })
  }

  useCustomFetch('quotation', {
    method: 'POST',
    body: formData
  }, true).then((data) => {
    success.value = true
    store.clearProducts()

    if (typeof (dataLayer) != "undefined" && dataLayer !== null) {
      dataLayer?.push({
        ecommerce: null
      });

      dataLayer?.push({
        'event': 'quotationSent',
        'ecommerce': {
          transaction_id: data.reference,
          revenue: data.total,
          value: data.total,
          tax: Math.round(data.total - (data.total * 0.8)).toFixed(2),
          currency: currencyStore.currency,
          total_price: data.total,
          items: data.products
        }
      });
    }
  }).catch((error) => {
    const messages = error?.data?.errors ?? error?.data?.data?.errors
    const flatMessages = messages ? Object.values(messages).flat() : []

    if (!flatMessages.length) {
      quotationErrors.value = [t('quotation.error')]
      return
    }

    flatMessages.forEach(message => {
      quotationErrors.value.push(message)
    })
  })
}
</script>

<template>
  <SideSlider @set-container-ref="container=$event" @close="success=false; store.closeQuotation();" position="right"
              width="480px"
              :title="$t('quotation.company-quotation')"
              :active="store.quotationIsActive">

    <div class="g-16">
      <div v-if="!success" class="row flex-start"
           style="overflow-x: hidden; max-height: 100%; max-width: 480px;">
        <div class="slide g-24" :class="{active:addProductMode}">
          <Form submit-padding="0 24px" class="form" @submit="send" ref="form">

            <template #default>
              <div class="g-16">
                <div class="g-32">
                  <div class="quotation-container no-flex g-16">
                    <FormInput :with-validation-approve="true" type="select" v-model="store.form.type"
                               :label="$t('form.fields.type')"
                               name="client_data[type]"
                               :options="[{key:'private',value:$t('form.fields.private')},{key:'company',value:$t('form.fields.company')}]"/>
                    <InputsGroup>
                      <FormInput :with-validation-approve="true" :validations="['required']" type="text"
                                 :label="$t('form.fields.first_name')"
                                 name="client_data[first_name]"
                                 v-model="store.form.firstName"/>
                      <FormInput :with-validation-approve="true" :validations="['required']" type="text"
                                 :label="$t('form.fields.last_name')"
                                 name="client_data[last_name]"
                                 v-model="store.form.lastName"/>
                    </InputsGroup>
                    <InputsGroup v-if="store.form.type === 'company'">
                      <FormInput :with-validation-approve="true" :validations="['required']" type="text"
                                 :label="$t('form.fields.company_name')"
                                 name="client_data[company_name]"
                                 v-model="store.form.companyName"/>
                      <FormInput :with-validation-approve="true"
                                 :validations="companyNumberValidations"
                                 type="text"
                                 :label="$t('form.fields.company_number')"
                                 name="client_data[company_number]"
                                 v-model="store.form.companyNumber"/>
                    </InputsGroup>
                    <FormInput v-if="store.form.type === 'company'"
                               :with-validation-approve="true" type="select" v-model="store.form.companyType"
                               :label="$t('form.fields.company_type')"
                               name="client_data[company_type]"
                               :options="[
                                     {key:$t('form.fields.company_type_values.0'),value:$t('form.fields.company_type_values.0')},
                                     {key:$t('form.fields.company_type_values.1'),value:$t('form.fields.company_type_values.1')},
                                     {key:$t('form.fields.company_type_values.2'),value:$t('form.fields.company_type_values.2')},
                                     {key:$t('form.fields.company_type_values.3'),value:$t('form.fields.company_type_values.3')},
                                     {key:$t('form.fields.company_type_values.4'),value:$t('form.fields.company_type_values.4')},
                                     {key:$t('form.fields.company_type_values.5'),value:$t('form.fields.company_type_values.5')},
                                     {key:$t('form.fields.company_type_values.6'),value:$t('form.fields.company_type_values.6')},
                                   ]"/>
                    <InputsGroup>
                      <FormInput :with-validation-approve="true" :validations="['required']" type="text"
                                 :label="$t('form.fields.phone')"
                                 name="client_data[phone]"
                                 v-model="store.form.phone"/>
                      <FormInput :with-validation-approve="true" :validations="['required','email']" type="email"
                                 :label="$t('form.fields.email')"
                                 name="client_data[email]"
                                 v-model="store.form.email"/>

                    </InputsGroup>
                    <FormInput :with-validation-approve="true" :validations="['required','address_not_valid']" type="text"
                               :label="$t('form.fields.delivery_address')"
                               name="client_data[delivery_address]"
                               v-model="store.form.address"/>
                    <InputsGroup>
                      <FormInput :with-validation-approve="true" :validations="['required']" type="text"
                                 :label="$t('form.fields.zip')"
                                 name="client_data[zip]" v-model="store.form.zip"/>
                      <FormInput type="select" v-model="store.form.country" :label="$t('form.fields.country')"
                                 name="client_data[country]"
                                 :options="startupStore.countries.map(country => ({
                        key: country.code,
                        value: country.name,
                        prefix: `<img style='width: 20px;' src='${country.flag}'/>`
                     }))"/>
                    </InputsGroup>

                    <Disclaimer v-if="hasCustomCountertop" type="info"
                                :text="$t('quotation.countertop_disclaimer')"/>

                    <Text v-if="!hasCustomCountertop && !addComment" class="clickable" tag="strong"
                          @click="addComment=true">{{ $t('base.add_comment') }}
                    </Text>

                    <FormInput v-if="hasCustomCountertop || addComment" type="textarea" v-model="store.form.comment"
                               :label="$t('form.fields.comment')"
                               name="comment"/>
                    <FormInput v-if="hasCustomCountertop" type="file" @filesChanged="store.form.files = $event.files"
                               :label="$t('form.fields.files')"/>
                  </div>

                  <div class="quotation-container no-flex g-16">
                    <Text class="info-text" v-if="store.form.type === 'private'">{{ $t('quotation.disclaimer') }}</Text>
                    <template v-for="(product, index) in store.products" :key="product.sku">
                      <CartProductItem :link="'/shop/'+product.url" :name="product.name" :quantity="product.quantity"
                                       :sku="product.sku" :image="product.image" :unit="product.unit"
                                       :unit_per_box="product.unit_per_box" :is_sample="false"
                                       @minus="product.quantity > 1 ? product.quantity-- : false"
                                       @remove="store.removeProduct(product.sku)"
                                       @change="product.quantity=$event.target.value"
                                       @plus="product.quantity++"
                      >
                        <template v-slot:under>
                          <template v-if="index === store.products.length - 1">
                            <Text v-for="error in quotationErrors" :key="error" class="error">{{ error }}</Text>
                          </template>
                        </template>
                      </CartProductItem>

                    </template>
                  </div>
                </div>
                <div class="quotation-container g-16">
                  <Text tag="strong" style="margin-top: -16px; margin-bottom: 24px;" class="clickable"
                        @click="addProductMode=true">+ {{
                      $t('product.add_product')
                    }}
                  </Text>
                  <Text class="info-text">{{ $t('quotation.text') }}</Text>
                  <Text class="red" v-if="!store.products.length && noProductsError">Vänligen lägg till
                    minst en product
                  </Text>
                </div>
              </div>
            </template>
            <template #submit>
              {{ $t('quotation.send') }}
            </template>
          </Form>
        </div>
        <div class="slide no-flex g-24">
          <div class="quotation-container">
            <div class="row full-width g-32">
              <div class="no-flex">
                <LeftIcon class="clickable" @click="addProductMode=false"/>
              </div>
              <FormInput type="text" :label="$t('product.quick_search_placeholder')" v-model="search"/>
            </div>
          </div>

          <div class="products" v-if="addProductMode">
            <div class="quotation-container">
              <div class="product row full-width stretch g-24" v-for="product in products">
                <div class="image">
                  <img :src="product.image" alt="product"/>
                </div>
                <div class="full-width space-between">
                  <div class="full-width row space-between flex-start">
                    <nuxt-link :to="'/shop/'+product.url">
                      <Text>{{ product.name }}</Text>
                    </nuxt-link>
                  </div>
                  <div class="space-between row full-width">
                    <Button @click="addProduct(product)" type="black" class="short" text="+ Add"/>
                    <Text class="sku">{{ product.sku }}</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="success flex-center g-16 quotation-container">
        <CircleCheckboxIcon/>

        <Text tag="strong">{{ $t('quotation.success.thank-you') }}</Text>

        <Text text-alignment="center">{{ $t('quotation.success.will_call_you_back') }}</Text>
        <Text text-alignment="center">{{ $t('quotation.success.disclaimer') }}</Text>
      </div>
    </div>
  </SideSlider>
</template>

<style scoped lang="scss">
.slide {
  min-width: 100%;
  transition: 0.3s all ease;
  padding-bottom: 30px;

  &.active {
    margin-left: -100%;
  }
}

.quotation-container {
  padding: 0 24px;
}

.products {
  background: $light-background;

  .product {
    padding: 12px 0;
  }
}

.image {
  max-width: 68px;
}

.info-text {
  color: $light-black;
  font-size: 12px;
}

.success {
  svg {
    stroke: $green;
    width: 64px;
    height: 64px;
  }

  strong {
    font-size: 24px;
  }
}

.form:deep(button) {
  font-size: 14px;
}

.error {
  color: $red;
  font-size: 12px;
  margin-left: 87px;
  margin-top: 8px;
}
</style>
